import { GraviButton, Horizontal, MaterialIcon } from '@gravitate-js/excalibrr'
import { message, Popconfirm, Tooltip } from 'antd'
import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'

import { useTransactions } from '../../../../../api/useFreight/useTransactions'
import type { TransactionDetail } from '../../../../../api/useFreight/types'
import { AddFreightLineItemPopover } from '../AddFreightLineItem'
import { FileAddOutlined } from '@ant-design/icons'
import { PopconfirmButton } from '@/components/_SiteWide/PopconfirmButton'

/**
 * Renderer for order action buttons in the grid
 */
export function OrderActionsRenderer(params: any) {
  const { data, api, context } = params
  const isSent = data?.details.some((item: TransactionDetail) => item.status === 'posted')
  const queryClient = useQueryClient()
  const { useRefreshTransactionMutation } = useTransactions()
  const refreshMutation = useRefreshTransactionMutation
  const navigate = useNavigate()

  const handleRecalculate = async () => {
    try {
      await refreshMutation.mutateAsync({
        order_id: data.order_id,
        book_type: data.book_type,
      })
      message.success('Order recalculated successfully')
      setTimeout(() => {
        api.refreshCells({ force: true })
        api.redrawRows({ force: true })
      }, 1000)
    } catch (error) {
      message.error('Error recalculating order')
    }
  }

  const handleDrawerOpen = () => {
    // Use the context to access the setDrawerState function
    context?.setDrawerState?.({ visible: true, orderId: data.order_id })
  }

  return (
    <Horizontal gap={4} style={{ justifyContent: 'flex-end' }}>
      {!['estimated', 'error'].includes(data?.status) && (
        <Tooltip title='Add Freight Line Item'>
          <div style={{ display: 'inline-block' }}>
            <AddFreightLineItemPopover actionType='create' order={data} gridApi={api}>
              <GraviButton size='small' icon={<FileAddOutlined />} />
            </AddFreightLineItemPopover>
          </div>
        </Tooltip>
      )}
      {isSent && (
        <Tooltip title='Trigger Credit Rebill'>
          <GraviButton
            onClick={() => navigate(`/freight/invoices/${data.invoice_group_id}?triggerRebill=true`)}
            size='small'
            icon={<MaterialIcon className='pt-1' icon='currency_exchange' />}
          />
        </Tooltip>
      )}
      {!isSent && (
        <Tooltip title='Recalculate Line Items'>
          <div>
            <PopconfirmButton
              buttonProps={{
                size: 'small',
                loading: refreshMutation.isPending,
                icon: <MaterialIcon className='pt-1' icon='refresh' />,
              }}
              onConfirm={handleRecalculate}
              popconfirmProps={{
                title:
                  'Are you sure you want to recalculate the line items? This will override the current line items and recalculate them from scratch.',
              }}
            />
          </div>
        </Tooltip>
      )}
      {data?.status !== 'estimated' && data?.status !== 'error' && (
        <Tooltip title='View Freight Order Details'>
          <GraviButton
            size='small'
            icon={<MaterialIcon className='pt-1' icon='top_panel_close' />}
            onClick={handleDrawerOpen}
          />
        </Tooltip>
      )}
    </Horizontal>
  )
}
